import React, { useEffect, useMemo, useState } from 'react'
import { Document, Page, Text, View, Image, Link } from '@react-pdf/renderer'

import { formatDateTime } from '_views/finance/payment-advance/utils'
import PropTypes from 'prop-types'
import BudgetCover from './BudgetCover'

import styles from './styles'
import { round } from '_/views/finance/utils/functions'
import { MOVIDESK_LINKS } from '_/utils/constants'

const BudgetPDF = ({
  budget,
  property,
  serviceOrder,
  agencyLogo,
  dateBudget = null,
  installments,
  showReferaValue,
  agency,
  systemParameters,
}) => {
  const allowedExtensions = ['.jpeg', '.png', '.jpg']
  const formatterNumberOnly = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })

  const [coverageMessage, setCoverageMessage] = useState('')

  const shouldShowCover = agency?.budgetCover === true

  const budgetGeneralCover = systemParameters?.find(
    param => param.name === 'budget_general_cover'
  )?.value

  const coverImage = agency?.budgetCoverPicture || budgetGeneralCover || null

  const handleGetMessage = () => {
    const coverageAmount = budget?.totalPrice * (budget?.coverageValue?.coveragePercentage / 100)

    if (coverageAmount > 0) {
      setCoverageMessage(
        <>
          Este serviço possui a Cobertura da Refera! A refera se compromete a resolver qualquer
          problema relacionado ao serviço executado, desde que esteja dentro do prazo da garantia e
          dentro do limite de valor R${' '}
          {coverageAmount.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          .{' '}
          <Link
            href={MOVIDESK_LINKS.DIFFERENCE_PROVIDER_GUARANTEE_AND_REFERENCE_COVERAGE}
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            clique aqui
          </Link>{' '}
          para saber mais sobre a cobertura da refera.
        </>
      )
    } else if (isNaN(coverageAmount)) {
      setCoverageMessage(
        <>
          Este serviço possui a Cobertura especial da Refera! A refera se compromete a resolver
          qualquer problema relacionado ao serviço executado, desde que esteja dentro do prazo da
          garantia.{' '}
          <Link
            href={MOVIDESK_LINKS.WITH_REFERA_COVERAGE}
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            clique aqui
          </Link>{' '}
          para saber mais sobre essa política.
        </>
      )
    } else if (coverageAmount === 0) {
      setCoverageMessage(
        <>
          Este serviço não possui a cobertura da refera.{' '}
          <Link
            href={MOVIDESK_LINKS.WITHOUT_REFERA_COVERAGE}
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            clique aqui
          </Link>{' '}
          e entenda o que isso significa.
        </>
      )
    }
  }

  useEffect(() => {
    handleGetMessage()
  }, [budget])

  const filteredPictures = useMemo(
    () =>
      budget?.budgetPictures.filter(file =>
        allowedExtensions.some(format => file?.picture?.includes(format))
      ) || [],
    [budget?.budgetPictures]
  )

  const handlePercentageToValue = (percentage, totalReference) => {
    return (percentage * totalReference) / 100.0
  }

  // Percentagem da refera
  const referaPlusAgencyPercentage = useMemo(() => {
    const agencyPercentage = (budget?.agencyCompletionPercentage || 0) / 100
    const referaPercentage = (budget?.referaCompletionPercentage || 0) / 100

    const totalPercentage = agencyPercentage + referaPercentage

    return Math.round(totalPercentage * 100)
  }, [budget])

  const tradesmanPercentage = useMemo(() => {
    const totalPercentage = referaPlusAgencyPercentage
    return 100 - totalPercentage
  }, [referaPlusAgencyPercentage])

  const itemValueCalculate = (itemValue, index = undefined) => {
    const revenueShare = budget?.revenueShare || 0
    const priceRefera = (budget?.priceRefera || 0) - revenueShare
    const priceAgency = budget?.priceAgency || 0
    const priceServiceProvider = budget?.priceServiceProvider || 0
    const totalBase = priceServiceProvider + revenueShare

    const percentageToAdd = ((priceRefera + priceAgency) * 100) / totalBase

    let itemPrice = handlePercentageToValue(percentageToAdd, itemValue) + itemValue

    itemPrice = round(itemPrice, 2)

    return index !== undefined ? formatterNumberOnly.format(itemPrice) : itemPrice
  }

  const handleTotalItems = useMemo(() => {
    const items = budget?.budgetItems ?? []
    return items.reduce((sum, { price }) => sum + itemValueCalculate(price), 0)
  }, [budget?.budgetItems, itemValueCalculate])

  const isNeutralContractType =
    serviceOrder?.agency?.get('contractType') !== 'i_got_rid' &&
    serviceOrder?.agency?.get('contractType') !== 'i_answer'

  return (
    <Document>
      {shouldShowCover && coverImage && <BudgetCover coverImage={coverImage} />}

      <Page size="A4" style={styles.page}>
        <View style={isNeutralContractType ? styles.neutralBackground : styles.header} fixed>
          <View>
            {isNeutralContractType &&
            agencyLogo &&
            serviceOrder?.agency?.get('contractType') !== 'SAAS' ? (
              <Image style={styles.headerLogo} src={agencyLogo} />
            ) : null}
          </View>
        </View>
        {agencyLogo && <Image style={styles.agencyLogo} src={agencyLogo} />}
        <View style={styles.pageSpace}>
          <Text style={isNeutralContractType ? styles.grayText : styles.blackText}>
            Orçamento <span style={styles.gray32}>•</span> {serviceOrder?.agency.get('name')}
          </Text>
          <View>
            <Text style={styles.headerText}>
              Chamado #{budget?.serviceOrder}, orçamento #{budget?.id}
            </Text>
          </View>
          <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
            <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
              Dados básicos
            </Text>
          </View>
          <View style={styles.budgetContainer}>
            <View style={styles.descriptionText}>
              <Text style={styles.title}>Data da emissão do orçamento</Text>
              <Text style={styles.description}>{formatDateTime(dateBudget)}</Text>
            </View>
            {budget?.tradesman?.companyName && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Prestador</Text>
                <Text style={styles.description}>
                  {`${budget.tradesman.companyName} - ${budget.tradesman.companyCnpj}`}
                </Text>
              </View>
            )}
            <View style={styles.descriptionText}>
              <Text style={styles.title}>Endereço do imóvel</Text>
              <Text style={styles.description}>{property}</Text>
            </View>
            <View style={styles.descriptionText}>
              <Text style={styles.title}>Código do imóvel</Text>
              <Text style={styles.description}>{serviceOrder?.propertyCode}</Text>
            </View>
            <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
              <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
                Dados do serviço
              </Text>
            </View>
            <View style={styles.serviceData}>
              <Text style={styles.title}>Diagnóstico do problema</Text>
              <Text style={styles.description}>{budget?.diagnosis}</Text>
            </View>
            <View style={styles.serviceData}>
              <Text style={styles.title}>Solução do problema</Text>
              <Text style={styles.description}>{budget?.proposedSolution}</Text>
            </View>
            <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
              <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
                Orçamento detalhado
              </Text>
            </View>
            <View style={styles.tableContainer}>
              <View style={styles.rowContainer}>
                <Text style={[styles.rowDescription, styles.gray80]}>Descrição</Text>
                <Text style={[styles.rowValues, styles.gray80]}>Valores</Text>
              </View>
              {budget?.budgetItems
                ?.sort((a, b) => a.id - b.id)
                ?.map((item, index) => (
                  <View style={styles.rowContainer} key={item.id}>
                    <View style={styles.rowDescription}>
                      <Text>{item?.description}</Text>
                    </View>
                    <View style={styles.rowValues}>
                      <Text>{itemValueCalculate(item?.price, index)}</Text>
                    </View>
                  </View>
                ))}
              <View style={styles.finalValuesContainer}>
                <View style={[styles.rowContainer, styles.totalBackground]}>
                  <Text style={[styles.rowDescriptionTotal, styles.gray80]}>Total</Text>
                  <Text style={[styles.rowValuesTotal, styles.gray100]}>
                    {formatterNumberOnly.format(handleTotalItems)}
                  </Text>
                </View>
                {budget?.withholdingTaxValue > 0 && (
                  <>
                    <View style={styles.rowContainer}>
                      <View style={styles.rowDescription}>
                        <Text>Imposto retido: </Text>
                      </View>
                      <View style={styles.rowValues}>
                        <Text>{formatterNumberOnly.format(budget?.withholdingTaxValue)}</Text>
                      </View>
                    </View>
                    <View style={[styles.rowContainer, styles.totalBackground]}>
                      <Text style={[styles.rowDescriptionTotal, styles.gray80]}>Total geral:</Text>
                      <Text style={[styles.rowValuesTotal, styles.gray100]}>
                        {formatterNumberOnly.format(handleTotalItems - budget?.withholdingTaxValue)}
                      </Text>
                    </View>
                  </>
                )}
              </View>
            </View>
            {showReferaValue && (
              <View style={styles.referaValueContainer}>
                <Text>
                  *{referaPlusAgencyPercentage}% referente à suporte ao longo de todas as etapas do
                  serviço, gestão de prestadores, orçamentação, e garantia de obra {'\n'}*
                  {tradesmanPercentage}% referente a materiais e mão de obra.
                </Text>
              </View>
            )}
            <View style={styles.titleUnbreakable}>
              <Text style={styles.installmentLabelContainer}>Parcelamento</Text>
              <View style={styles.inputInstallmentsText}>
                {installments?.length >= 1 ? (
                  <>
                    {installments?.map(installment => (
                      <View style={styles.description} key={installment}>
                        <Text>{installment.option}</Text>
                      </View>
                    ))}
                  </>
                ) : null}
              </View>
            </View>
            <View>
              <Text style={styles.installmentLabelContainer}>Garantia do prestador (meses)</Text>
              <View style={styles.inputInstallmentsText}>
                <Text>{budget?.warrantyMonths}</Text>
              </View>
              {isNeutralContractType ? null : (
                <>
                  {coverageMessage && (
                    <View style={styles.referaValueContainer}>
                      <Text>{coverageMessage}</Text>
                    </View>
                  )}
                </>
              )}
            </View>
            <View style={styles.otherInfosContainer}>
              <View style={styles.otherInfosItemContainer}>
                <Text style={styles.hidden}>Prazo para iniciar execução</Text>
                {/* <View style={[styles.input, styles.otherInfosInput]}> */}
                {/* <Text style={styles.budgetItemText}> */}
                {/* {budget?.deadlineStart === null ? 0 : budget?.deadlineStart} meses */}
                {/* </Text> */}
                {/* </View> */}
              </View>
            </View>
            {/* <View style={styles.observations}>
            <Text>Observações</Text>
            <View style={[styles.input, styles.inputTextArea]}>
              <Text style={styles.description}>{budget?.observations}</Text>
            </View>
          </View> */}
          </View>
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
      {Object.keys(filteredPictures).length !== 0 && (
        <Page size="A4" style={styles.page}>
          <View style={isNeutralContractType ? styles.neutralBackground : styles.header}>
            <View>
              {isNeutralContractType &&
              agencyLogo &&
              serviceOrder?.agency?.get('contractType') !== 'SAAS' ? (
                <Image style={styles.headerLogo} src={agencyLogo} />
              ) : null}
            </View>
          </View>
          <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
            <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>Anexos</Text>
          </View>
          <View>
            {filteredPictures.map(attachment => (
              <View style={styles.attachmentContainer} key={attachment.id}>
                <Image style={styles.attachmentImage} src={attachment.picture} />
              </View>
            ))}
          </View>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            fixed
          />
        </Page>
      )}
    </Document>
  )
}

BudgetPDF.propTypes = {
  budget: PropTypes.shape({
    budgetItems: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.number,
    serviceOrder: PropTypes.number,
    acceptsInstallment: PropTypes.bool,
    proposedSolution: PropTypes.string,
    deadlineStart: PropTypes.number,
    diagnosis: PropTypes.string,
    observations: PropTypes.string,
    installments: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    acceptsWarranty: PropTypes.bool,
    warrantyMonths: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    visitHappened: PropTypes.bool,
    totalPriceTradesman: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    referaCompletionPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    agencyCompletionPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    budgetPictures: PropTypes.arrayOf(PropTypes.shape({})),
    personPaying: PropTypes.string,
    personPayingEmail: PropTypes.string,
    personPayingWhatsapp: PropTypes.string,
    createdAt: PropTypes.string,
    financialIncome: PropTypes.number,
  }).isRequired,
  showReferaValue: PropTypes.bool.isRequired,
}

export default BudgetPDF
